import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Package, ShieldCheck, TriangleAlert, Leaf, Gauge } from "lucide-react";

const PERF = { low: "success", moderate: "warning", high: "destructive", unknown: "outline" };
const TIER_TONE = { A: "success", B: "warning", C: "outline", D: "destructive" };

function DqBar({ dq }) {
  const tone = dq.score >= 75 ? "#00E676" : dq.score >= 50 ? "#FFB800" : "#FF5252";
  return (
    <div className="flex items-center gap-2" title="Lestari data-confidence indicator (not a GHG Protocol rating)">
      <div className="h-1.5 w-16 rounded-full bg-surface overflow-hidden"><div className="h-full rounded-full" style={{ width: `${dq.score}%`, background: tone }} /></div>
      <span className="font-mono text-[10px]">{dq.score}</span>
    </div>
  );
}

export default function ProductsTraceability() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/scope3-bridge/products").then((r) => setData(r.data)).catch(() => setData({ suppliers: [] })); }, []);
  if (!data) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading products…</div>;

  return (
    <div className="p-6 space-y-5" data-testid="products-traceability-page">
      <div>
        <div className="label-eyebrow mb-1">Supply Chain</div>
        <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><Package className="h-6 w-6 text-accent" /> Products & Traceability</h2>
        <p className="text-muted-foreground text-sm mt-1 max-w-2xl">Supplier → product/SKU → footprint. Carbon performance (how intense) and data confidence (how trusted) are shown as two separate indicators. One supplier can make very different products.</p>
      </div>
      {data.suppliers.filter((s) => s.products.length).map((s) => (
        <div key={s.supplier_id} className="card-flat p-5" data-testid={`prod-supplier-${s.supplier_id}`}>
          <div className="flex items-center justify-between mb-3">
            <div><div className="font-display font-bold">{s.supplier_name}</div><div className="text-[11px] text-muted-foreground font-mono">{s.country || "—"} · {s.products.length} product(s)</div></div>
            <Leaf className="h-4 w-4 text-accent" />
          </div>
          <div className="space-y-2">
            {s.products.map((p) => (
              <div key={p.id} className="p-3 rounded-sm border border-border bg-surface" data-testid={`prod-${p.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-sm font-medium">{p.name}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{p.scope3_category}{p.period ? ` · FY${p.period}` : ""}</div>
                  </div>
                  <Badge variant={TIER_TONE[p.method_tier]} className="text-[9px]" title="Calculation method tier (A–D)">Method {p.method_tier}</Badge>
                  <div className="text-right"><div className="font-mono text-sm font-bold">{p.co2e_tonnes ?? "—"}</div><div className="text-[9px] text-muted-foreground">tCO₂e</div></div>
                </div>
                <div className="flex items-center gap-4 mt-2 flex-wrap text-[11px]">
                  <span className="inline-flex items-center gap-1"><Gauge className="h-3 w-3 text-muted-foreground" /> Carbon performance <Badge variant={PERF[p.carbon_performance.rating]} className="text-[8px] uppercase">{p.carbon_performance.rating}</Badge>{p.carbon_performance.intensity_per_unit != null && <span className="font-mono text-muted-foreground">{p.carbon_performance.intensity_per_unit} {p.carbon_performance.unit}</span>}</span>
                  <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-muted-foreground" /> Data confidence <DqBar dq={p.data_quality} /></span>
                  {p.pcf?.verified ? <Badge variant="success" className="text-[8px] uppercase">Verified PCF</Badge> : p.pcf ? <Badge variant="outline" className="text-[8px] uppercase">Unverified PCF</Badge> : <Badge variant="outline" className="text-[8px] uppercase">No PCF — estimated</Badge>}
                  {p.cbam_relevant && <Badge variant="warning" className="text-[8px] uppercase inline-flex items-center gap-1" title="CBAM stays a separate regulatory domain — reconcile before feeding the inventory"><TriangleAlert className="h-3 w-3" /> CBAM</Badge>}
                </div>
                {p.pcf && (
                  <div className="text-[10px] text-muted-foreground mt-1.5 font-mono">
                    PCF (original supplied): {p.pcf.footprint_per_unit} {p.pcf.declared_unit} · {p.pcf.boundary} · {p.pcf.method} · {p.pcf.geography} FY{p.pcf.period} · PACT-compatible
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {!data.suppliers.some((s) => s.products.length) && <div className="card-flat p-10 text-center text-muted-foreground text-sm">No supplier products captured yet.</div>}
    </div>
  );
}
